import {
  useContext,
  createContext,
  useState,
  useCallback,
  useEffect,
} from "react";
import { useHistory, useLocation } from "react-router-dom";
import useAbortSignal from "../hooks/useAbortSignal";

import { errorToast } from "../util/toastNotifications";
import AuthServices from "../services/authServices";

const AuthContext = createContext();

const PUBLIC_ROUTES = [
  "/login",
  "/register",
  "/login/forgot-password",
  "/login/change-password",
  "/login/email-verification",
  "/login/verify-email",
  "/login/verify-account",
];

const getSafeRedirectPath = (path, userId) => {
  if (!path || !userId) return "/home";

  const userViewMatch = path.match(/^\/user\/([^/]+)$/);
  if (userViewMatch && userViewMatch[1] !== userId) {
    return "/home";
  }

  const userEditMatch = path.match(/^\/user-add\/([^/]+)$/);
  if (userEditMatch && userEditMatch[1] !== userId) {
    return "/home";
  }

  return path;
};

export const AuthContextProvider = ({ children }) => {
  const history = useHistory();
  const { signal } = useAbortSignal();
  const location = useLocation();

  const [user, setUser] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [redirectLink, setRedirectLink] = useState("/home");

  const updateUserInfo = useCallback((data) => {
    setUser((previous) =>
      typeof data === "function" ? data(previous) : data
    );
  }, []);

  const login = useCallback(
    async (payload) => {
      setIsLoading(true);

      try {
        const { user_info } = await AuthServices.loginService(payload, signal);
        setUser(user_info);
        if (location.pathname === "/login") {
          history.push(getSafeRedirectPath(redirectLink, user_info.user_id));
        }
      } catch (error) {
        setErrorMsg(error.message);
      } finally {
        setIsLoading(false);
      }
    },
    [history, signal, redirectLink, location.pathname]
  );

  const logout = useCallback(
    async (options = {}) => {
      const { suppressErrorToast } = options;
      setIsLoading(true);

      try {
        const isLoggedOut = await AuthServices.logoutService(signal);
        if (isLoggedOut) {
          setUser(null);
          setRedirectLink("/home");
          localStorage.removeItem("breadCrumbTrail");
        }
      } catch (error) {
        setUser(null);
        setRedirectLink("/home");
        if (!suppressErrorToast) {
          errorToast("Unable to logout");
        }
      } finally {
        setIsLoading(false);
        setTimeout(() => history.push("/login"), 0);
      }
    },
    [history, signal]
  );

  useEffect(() => {
    let mounted = true;

    const validateSession = async () => {
      try {
        const authData = await AuthServices.checkLogin();

        if (!mounted) return;

        const userInfo = authData?.results?.user_info || authData?.user_info;

        if (userInfo) {
          setUser(userInfo);
          const isPublicRoute = PUBLIC_ROUTES.some(
            (route) =>
              location.pathname === route ||
              location.pathname.startsWith(route + "/")
          );
          if (!isPublicRoute) {
            setRedirectLink(
              getSafeRedirectPath(location.pathname, userInfo.user_id)
            );
          }
        } else {
          setUser(null);
          if (
            !PUBLIC_ROUTES.some(
              (route) =>
                location.pathname === route ||
                location.pathname.startsWith(route + "/")
            )
          ) {
            history.push("/login");
          }
        }
      } catch (e) {
        if (mounted) {
          setUser(null);
          if (
            !PUBLIC_ROUTES.some(
              (route) =>
                location.pathname === route ||
                location.pathname.startsWith(route + "/")
            )
          ) {
            history.push("/login");
          }
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    validateSession();
    return () => {
      mounted = false;
    };
  }, []);

  const values = {
    userInfo: user,
    isLoading,
    errorMsg,
    login,
    logout,
    updateUserInfo,
  };

  return <AuthContext.Provider value={values}>{children}</AuthContext.Provider>;
};

export const useAuthInfo = () => {
  return useContext(AuthContext);
};

export { AuthContext };
